import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'country',
    'programLevel',
    'duration',
    'licenseDetailId',
    'billingComponentId',
    'conditionalField',
    'languageText',
    'languageSelect',
    'outreachEligible',
  ]
  static values = {
    programsMap: Object,
    countryLanguages: Object,
  }

  connect() {
    const hasLegacyId =
      this.hasLicenseDetailIdTarget && this.licenseDetailIdTarget.value
    const hasBillingId =
      this.hasBillingComponentIdTarget && this.billingComponentIdTarget.value
    if (hasLegacyId || hasBillingId) {
      this.preselectFromLicenseDetailId()
    } else if (this.hasCountryTarget && this.countryTarget.value) {
      this.populateProgramLevels()
    }
    this.updateConditionalFields()
    this.updateLanguageField()
  }

  companyChanged(e) {
    const companyId = e.target.value
    this.resetSelects()
    if (!companyId) {
      this.programsMapValue = {}
      this.rebuildCountries()
      return
    }

    const queryParams = new URLSearchParams({ company_id: companyId })
    fetch(`/outplacement/global/programs?${queryParams.toString()}`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok')
        return response.json()
      })
      .then((data) => {
        this.programsMapValue = data.programs_map || {}
        this.rebuildCountries()
        return null
      })
      .catch((error) => {
        console.error('Error fetching programs:', error)
      })
  }

  countryChanged() {
    this.populateProgramLevels()
    this.clearDuration()
    this.clearLicenseDetailId()
    this.updateConditionalFields()
    this.updateLanguageField()
  }

  updateLanguageField() {
    if (!this.hasLanguageTextTarget || !this.hasLanguageSelectTarget) return

    const country = this.hasCountryTarget ? this.countryTarget.value : ''
    const languages = this.countryLanguagesValue[country]

    if (languages) {
      this.showLanguageSelect(languages)
    } else {
      this.showLanguageText()
    }
  }

  showLanguageSelect(languages) {
    const current =
      this.languageSelectTarget.value || this.languageTextTarget.value

    this.languageSelectTarget.innerHTML =
      '<option value=""> - Select - </option>'
    languages.forEach((language) => {
      const option = document.createElement('option')
      option.value = language
      option.textContent = language
      if (language === current) option.selected = true
      this.languageSelectTarget.appendChild(option)
    })

    this.languageSelectTarget.classList.remove('hidden')
    this.languageSelectTarget.disabled = false
    this.languageTextTarget.classList.add('hidden')
    this.languageTextTarget.disabled = true
  }

  showLanguageText() {
    if (!this.languageTextTarget.value && this.languageSelectTarget.value) {
      this.languageTextTarget.value = this.languageSelectTarget.value
    }

    this.languageTextTarget.classList.remove('hidden')
    this.languageTextTarget.disabled = false
    this.languageSelectTarget.classList.add('hidden')
    this.languageSelectTarget.disabled = true
  }

  updateConditionalFields() {
    if (!this.hasConditionalFieldTarget || !this.hasCountryTarget) return

    const required = Object.keys(this.countryLanguagesValue).includes(
      this.countryTarget.value,
    )
    this.conditionalFieldTargets.forEach((field) => {
      field.required = required
    })
  }

  programLevelChanged() {
    this.populateDurations()
    this.clearLicenseDetailId()
  }

  durationChanged() {
    this.resolveLicenseDetailId()
  }

  populateProgramLevels() {
    const country = this.countryTarget.value
    this.programLevelTarget.innerHTML = ''
    const defaultOption = document.createElement('option')
    defaultOption.value = ''
    defaultOption.textContent = ' - Select - '
    this.programLevelTarget.appendChild(defaultOption)

    if (!country || !this.programsMapValue[country]) return

    Object.keys(this.programsMapValue[country].levels).forEach((level) => {
      const option = document.createElement('option')
      option.value = level
      option.textContent = level.charAt(0) + level.slice(1).toLowerCase()
      this.programLevelTarget.appendChild(option)
    })
  }

  populateDurations() {
    const country = this.countryTarget.value
    const level = this.programLevelTarget.value
    this.durationTarget.innerHTML = ''
    const defaultOption = document.createElement('option')
    defaultOption.value = ''
    defaultOption.textContent = ' - Select - '
    this.durationTarget.appendChild(defaultOption)

    if (!country || !level) return

    const durations = this.programsMapValue?.[country]?.levels?.[level] || []
    durations.forEach(({ months }) => {
      const option = document.createElement('option')
      option.value = months
      option.textContent = months === 1 ? '1 month' : `${months} months`
      this.durationTarget.appendChild(option)
    })
  }

  resolveLicenseDetailId() {
    const country = this.countryTarget.value
    const level = this.programLevelTarget.value
    const months = parseInt(this.durationTarget.value)

    const durations = this.programsMapValue?.[country]?.levels?.[level] || []
    const detail = durations.find((d) => d.months === months)

    if (detail?.source === 'billing') {
      this.licenseDetailIdTarget.value = ''
      if (this.hasBillingComponentIdTarget) {
        this.billingComponentIdTarget.value = detail ? detail.id : ''
      }
    } else {
      this.licenseDetailIdTarget.value = detail ? detail.id : ''
      if (this.hasBillingComponentIdTarget) {
        this.billingComponentIdTarget.value = ''
      }
    }

    this.updateOutreachEligibleOptions(detail?.delivery_mode)
  }

  // A hybrid country's row (see Billing::ContractProgram::HYBRID_DELIVERY_COUNTRIES)
  // may be configured for domestic (YNS direct) or global (CSG partner) delivery.
  // Global delivery always requires Outreach Eligible = Yes (CSGHub restriction,
  // enforced server-side too); domestic delivery allows the same Yes/No choice as
  // the US form. Legacy (non-billing) rows have no delivery_mode and are always
  // treated as global.
  updateOutreachEligibleOptions(deliveryMode) {
    if (!this.hasOutreachEligibleTarget) return

    const allowNo = deliveryMode === 'domestic'
    const current = this.outreachEligibleTarget.value

    this.outreachEligibleTarget.innerHTML = ''

    const blank = document.createElement('option')
    blank.value = ''
    blank.textContent = ' - Select - '
    this.outreachEligibleTarget.appendChild(blank)

    const yes = document.createElement('option')
    yes.value = 'true'
    yes.textContent = 'Yes'
    this.outreachEligibleTarget.appendChild(yes)

    if (allowNo) {
      const no = document.createElement('option')
      no.value = 'false'
      no.textContent = 'No'
      this.outreachEligibleTarget.appendChild(no)
    }

    this.outreachEligibleTarget.value = allowNo ? current : 'true'
  }

  rebuildCountries() {
    const map = this.programsMapValue || {}
    const codes = Object.keys(map)
    this.countryTarget.innerHTML = ''
    const defaultOption = document.createElement('option')
    defaultOption.value = ''
    defaultOption.textContent = codes.length
      ? ' - Choose a Country - '
      : ' - No Programs Available - '
    this.countryTarget.appendChild(defaultOption)

    codes.forEach((code) => {
      const option = document.createElement('option')
      option.value = code
      option.textContent = map[code].name
      this.countryTarget.appendChild(option)
    })

    this.updateConditionalFields()
    this.updateLanguageField()
  }

  preselectFromLicenseDetailId() {
    const legacyId = parseInt(this.licenseDetailIdTarget.value)
    const billingId = this.hasBillingComponentIdTarget
      ? parseInt(this.billingComponentIdTarget.value)
      : null
    const id = legacyId || billingId
    if (!id) return

    const map = this.programsMapValue || {}

    for (const country of Object.keys(map)) {
      const levels = map[country].levels || {}
      for (const level of Object.keys(levels)) {
        const match = levels[level].find((d) => d.id === id)
        if (!match) continue

        this.countryTarget.value = country
        this.populateProgramLevels()
        this.programLevelTarget.value = level
        this.populateDurations()
        this.durationTarget.value = match.months
        this.updateOutreachEligibleOptions(match.delivery_mode)
        return
      }
    }
  }

  resetSelects() {
    this.clearProgramLevels()
    this.clearDuration()
    this.clearLicenseDetailId()
  }

  clearProgramLevels() {
    this.programLevelTarget.innerHTML = '<option value=""> - Select - </option>'
  }

  clearDuration() {
    this.durationTarget.innerHTML = '<option value=""> - Select - </option>'
  }

  clearLicenseDetailId() {
    this.licenseDetailIdTarget.value = ''
    if (this.hasBillingComponentIdTarget) {
      this.billingComponentIdTarget.value = ''
    }
    this.updateOutreachEligibleOptions(undefined)
  }
}
